/**
 * @engine/core - Viewport Types (portable)
 *
 * Shared interfaces for viewport rendering, used by both editor and player.
 *
 * Editor-specific types (ViewportSettings, ViewportController, ViewportManager)
 * remain in @engine/editor.
 *
 * @module @engine/core
 */

import type { IRenderContext } from '@engine/render2d';
import type { Level } from '../Level';

/**
 * Camera state for viewport
 */
export interface ViewportCameraState {
  /** World X position */
  x: number;
  /** World Y position */
  y: number;
  /** Zoom level (1 = 100%) */
  zoom: number;
  /** Rotation in radians */
  rotation: number;
}

/**
 * Viewport render request
 */
export interface ViewportRenderRequest {
  /** Target render context */
  ctx: IRenderContext;
  /** Viewport dimensions */
  width: number;
  height: number;
  /** Camera state */
  camera: ViewportCameraState;
  /** Current level to render */
  level: Level | null;
  /** Selected entity GUIDs */
  selectedGuids: Set<string>;
  /** Current time */
  time: number;
  /** Delta time */
  deltaTime: number;
  /** Is play mode active */
  isPlayMode: boolean;
}

/**
 * Viewport render result
 */
export interface ViewportRenderResult {
  /** Number of draw calls */
  drawCalls: number;
  /** Number of sprites rendered */
  spriteCount: number;
  /** Render time in ms */
  renderTimeMs: number;
}

/**
 * FPS limiter for viewport throttling
 */
export class FPSLimiter {
  private targetFps: number;
  private lastRenderTime = 0;
  private frameInterval: number;

  constructor(targetFps: number = 60) {
    this.targetFps = targetFps;
    this.frameInterval = 1000 / targetFps;
  }

  setTargetFps(fps: number): void {
    this.targetFps = Math.max(1, fps);
    this.frameInterval = 1000 / this.targetFps;
  }

  shouldRender(now: number = performance.now()): boolean {
    return now - this.lastRenderTime >= this.frameInterval;
  }

  markRendered(): void {
    this.lastRenderTime = performance.now();
  }

  reset(): void {
    this.lastRenderTime = 0;
  }

  getTimeUntilNextFrame(): number {
    const now = performance.now();
    const elapsed = now - this.lastRenderTime;
    return Math.max(0, this.frameInterval - elapsed);
  }

  get currentTargetFps(): number {
    return this.targetFps;
  }
}
